import { useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  buildIntensityGridFromDraft,
  MAX_SESSIONS,
  roundTypeAt,
  type CurrentMax,
  type Draft,
  type UnitsPreference,
} from "@heraion/shared";

import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { AddDayChip } from "../IntensityGrid/GridAddChips";
import { IntensityGridView } from "../IntensityGrid/IntensityGridView";
import type { ConfirmFn } from "../seams";
import type { UiTheme } from "../theme";
import type { V3Navigation } from "./useV3Navigation";
import type { V3DayActions } from "./useV3DayActions";
import { RoundMenu, type RoundMenuState } from "./RoundMenu";
import { RoundTypeBadge, useRoundTypeWords } from "../primitives/RoundTypeBadge";

// The top of the phone editor (#336, brief §7.2 and §7.4): the day selector
// D1..Dn, and under it the heatmap as overview and navigator. A cell takes
// you to its day and round. The heatmap's own structure row (D+, W+, hold
// W+ to repeat a round, hold a header to remove it) is reused as it is; a
// tap on a W head opens the round menu (3b). A deload or test round carries
// its glyph under the W (4d). On a wide screen the heatmap goes: the day's
// grid carries its bands and its structure (brief §15 row 28). From 768 to
// 1100 the chips stay, with «D ＋» beside them; from 1100 the structure
// column selects the day, and the chips go too (slice 6).
export function DayNavigator({
  theme,
  dark,
  draft,
  nav,
  days,
  confirm,
  dayChips = true,
  heatmap = true,
  maxMeta,
  cellUnit,
}: {
  theme: UiTheme;
  dark: boolean;
  draft: Draft;
  nav: V3Navigation;
  days: V3DayActions;
  confirm: ConfirmFn;
  /** False: no D1..Dn chips, the heatmap alone. */
  dayChips?: boolean;
  /** False (wide): the chips alone, with «D ＋». */
  heatmap?: boolean;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  cellUnit: UnitsPreference;
}) {
  const { t } = useTranslation();
  // The wide grid's band rule: a row in kilos takes its band from the MAX.
  const grid = useMemo(() => buildIntensityGridFromDraft(draft, { maxes: maxMeta, cellUnit }), [draft, maxMeta, cellUnit]);
  const indices = draft.sessions.map((_, i) => i);
  const canAddDay = draft.sessions.length < MAX_SESSIONS;
  const repeat = days.repeatRound;
  const [roundMenu, setRoundMenu] = useState<RoundMenuState>(null);
  const words = useRoundTypeWords();
  const typeOf = (w: number) => roundTypeAt(draft, w);
  const chips = dayChips ? (
    <ChipChoiceRow
      theme={theme}
      scroll={heatmap}
      edgeInset={heatmap ? theme.spacing.s5 : 0}
      choices={indices}
      selected={nav.dayIdx}
      onSelect={nav.selectDay}
      label={(i) => `D${i + 1}`}
      chipA11yLabel={(i) =>
        t("editorV3.dayChipA11y", {
          n: i + 1,
          name: draft.sessions[i]?.name ?? "",
          defaultValue: "Day {{n}} {{name}}",
        })
      }
      groupA11yLabel={t("editorV3.daysA11y", { defaultValue: "Days" })}
    />
  ) : null;
  if (!heatmap) {
    if (!chips) return null;
    // The heatmap's «D ＋», where there is neither heatmap nor structure column.
    return (
      <View style={[local.chipsRow, { gap: theme.spacing.s3, paddingHorizontal: theme.spacing.s5 }]}>
        <View style={local.chips}>{chips}</View>
        {canAddDay ? <AddDayChip theme={theme} onPress={days.add} /> : null}
      </View>
    );
  }
  return (
    <View>
      {chips}
      <View style={{ paddingHorizontal: theme.spacing.s5 }}>
        <IntensityGridView
          grid={grid}
          title={null}
          selected={nav.dayIdx}
          onSelect={nav.selectDay}
          selectedWeek={nav.roundIdx}
          onSelectCell={nav.selectCell}
          dark={dark}
          onAddDay={canAddDay ? days.add : undefined}
          onAddWeek={days.addRound}
          onCloneWeek={repeat && (() => repeat(nav.roundIdx))}
          onRemoveWeek={days.removeRound}
          onRemoveDay={days.removeConfirmed}
          confirm={confirm}
          onWeekPress={(week) => setRoundMenu({ week, mode: "menu" })}
          weekMark={(w) => {
            const type = typeOf(w);
            if (type === "normal") return null;
            return {
              badge: <RoundTypeBadge theme={theme} type={type} compact />,
              label: t("editorV3.roundChipTypedA11y", {
                n: w + 1,
                type: words[type],
                defaultValue: "Round {{n}}, {{type}}",
              }),
            };
          }}
        />
      </View>
      <View style={{ paddingHorizontal: theme.spacing.s5 }}>
        <RoundMenu
          theme={theme}
          state={roundMenu}
          setState={setRoundMenu}
          cycleWeeks={draft.cycleWeeks}
          typeOf={typeOf}
          days={days}
        />
      </View>
    </View>
  );
}

const local = StyleSheet.create({
  chipsRow: { flexDirection: "row", alignItems: "center" },
  chips: { flexShrink: 1 },
});
